import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff, Loader2, Lock, Mail, ShieldCheck, Sparkles, ChevronRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useOssAuth } from "../context/OssAuthContext";
import OssResponsabilePasswordDialog from "../components/oss/OssResponsabilePasswordDialog";
import AreaLoginShell from "../components/AreaLoginShell";
import { useOssNativeShell } from "../components/oss/useOssNativeShell";

const MARK_URL = "/images/logo-oss-mark.png";
const LOCKUP_URL = "/images/logo-oss.png";

const AVATAR_COLORS = [
  "bg-purple-500",
  "bg-emerald-500",
  "bg-rose-500",
  "bg-blue-500",
  "bg-amber-500",
  "bg-sky-500",
];

function avatarColor(id) {
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) | 0;
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function getInitials(s) {
  return `${s.nome?.[0] ?? ""}${s.cognome?.[0] ?? ""}`.toUpperCase();
}

export default function OssLogin() {
  useOssNativeShell();
  const { sessions, activeOss, switchTo, loginWithPassword } = useOssAuth();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [switchingId, setSwitchingId] = useState(null);
  const [passwordTarget, setPasswordTarget] = useState(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isAddMode = searchParams.get("add") === "1";

  if (activeOss && !isAddMode) {
    navigate("/oss", { replace: true });
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await loginWithPassword(identifier, password);
      toast.success("Accesso effettuato");
      navigate("/oss", { replace: true });
    } catch (error) {
      const detail = error.response?.data?.detail || "Credenziali non valide";
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const handleSwitch = async (id) => {
    setSwitchingId(id);
    try {
      const result = await switchTo(id);
      if (result?.needsPassword) {
        setPasswordTarget(result.session);
      }
    } finally {
      setSwitchingId(null);
    }
  };

  const handlePasswordConfirm = async (pwd) => {
    if (!passwordTarget?.id) return { ok: false };
    const result = await switchTo(passwordTarget.id, { password: pwd });
    if (result?.ok) {
      toast.success("Accesso effettuato");
    }
    return result;
  };

  const showSessionList = sessions.length > 0 && !isAddMode;

  return (
    <>
      <AreaLoginShell
        accent="sky"
        currentArea="oss"
        title="Area dedicata agli operatori socio-sanitari"
        subtitle="Consulta fascicoli, camere, turni e diario consegne dal tablet, con le credenziali fornite dalla struttura."
        logo={
          <img
            src={LOCKUP_URL}
            alt="OSS by Affidya"
            className="mx-auto mb-3 h-11 w-auto lg:mx-0 lg:mb-9 lg:h-12"
          />
        }
        features={[
          {
            icon: ShieldCheck,
            title: "Accesso riservato",
            text: "Vedi solo gli ospiti e i turni della tua struttura.",
          },
          {
            icon: Sparkles,
            title: "Pensato per il tablet",
            text: "Fascicoli, somministrazioni e diario sempre a portata di mano.",
          },
        ]}
      >
        <div className="mb-6 text-center">
          <img src={MARK_URL} alt="" className="mx-auto mb-4 h-14 w-14 rounded-2xl object-contain shadow-[0_8px_20px_-10px_rgba(3,105,161,0.6)]" />
          <h2 className="text-2xl font-bold tracking-tight text-slate-900">
            {showSessionList ? "Accedi con un altro utente" : "Accedi all'area OSS"}
          </h2>
          <p className="mt-1.5 text-sm text-slate-600">
            Email oppure codice univoco e password
          </p>
        </div>

        {showSessionList ? (
          <div className="mb-5 space-y-2">
            <p className="text-xs font-bold uppercase tracking-[0.08em] text-slate-500">Continua come</p>
            {sessions.map((s) => (
              <button
                key={s.id}
                type="button"
                className="flex w-full min-h-[60px] items-center gap-3 rounded-2xl border border-slate-200 bg-white px-3.5 py-2.5 text-left shadow-sm transition hover:border-sky-300 hover:bg-sky-50/50 touch-manipulation"
                onClick={() => handleSwitch(s.id)}
                disabled={switchingId === s.id}
              >
                <div
                  className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-semibold text-white ${avatarColor(s.id)}`}
                >
                  {switchingId === s.id ? (
                    <span className="h-5 w-5 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                  ) : (
                    getInitials(s)
                  )}
                </div>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-slate-800">
                    {s.nome} {s.cognome}
                  </span>
                  <span className={`block text-xs ${s.responsabile ? "font-medium text-amber-700" : "text-slate-500"}`}>
                    {s.responsabile ? "OSS Responsabile · richiede password" : "OSS"}
                  </span>
                </span>
                <ChevronRight className="h-4 w-4 shrink-0 text-slate-400" />
              </button>
            ))}
          </div>
        ) : null}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="oss-identifier" className="text-sm font-medium text-slate-700">
              Email o codice
            </Label>
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" aria-hidden />
              <Input
                id="oss-identifier"
                type="text"
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                enterKeyHint="next"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder="nome@email.it oppure ABCD-EFGH"
                className="h-12 rounded-2xl border-slate-200 bg-slate-50/60 pl-11 text-base focus-visible:ring-2 focus-visible:ring-sky-600/40"
                required
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="oss-password" className="text-sm font-medium text-slate-700">
              Password
            </Label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" aria-hidden />
              <Input
                id="oss-password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="h-12 rounded-2xl border-slate-200 bg-slate-50/60 pl-11 pr-12 text-base focus-visible:ring-2 focus-visible:ring-sky-600/40"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? "Nascondi password" : "Mostra password"}
                className="absolute right-0.5 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-xl text-slate-500 hover:text-slate-700 touch-manipulation"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <Button type="submit" disabled={loading} className="h-12 w-full rounded-2xl bg-sky-700 text-base font-semibold text-white shadow-[0_10px_24px_-12px_rgba(3,105,161,0.8)] hover:bg-sky-800">
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Accedi
          </Button>
        </form>
      </AreaLoginShell>

      <OssResponsabilePasswordDialog
        open={Boolean(passwordTarget)}
        session={passwordTarget}
        onClose={() => setPasswordTarget(null)}
        onConfirm={handlePasswordConfirm}
      />
    </>
  );
}
